import { createContext, useContext } from "react"
import { create, type StoreApi, type UseBoundStore } from "zustand"
import { persist } from "zustand/middleware"

export type DashboardNumberMode = "pct" | "abs"

export interface DashboardBoardState<K extends string> {
  mode: DashboardNumberMode
  setMode: (mode: DashboardNumberMode) => void
  order: K[]
  hidden: K[]
  moveWidget: (key: K, toIndex: number) => void
  hideWidget: (key: K) => void
  showWidget: (key: K) => void
}

/**
 * Rehydration merge: drops widget keys that no longer exist and appends new
 * ones, so persisted configs survive deploys that add/remove widgets.
 *
 * Exported (not just used internally by `createDashboardStore`) so a studio
 * store module can re-export a standalone, order-bound `mergeDashboardState`
 * function for direct unit testing — matching today's supplier module shape.
 */
export function createMergeDashboardState<K extends string>(defaultOrder: K[]) {
  return function mergeDashboardState(
    persisted: unknown,
    current: DashboardBoardState<K>,
  ): DashboardBoardState<K> {
    const p = (persisted ?? {}) as Partial<DashboardBoardState<K>>
    const known = new Set<string>(defaultOrder)
    const order = (p.order ?? []).filter((k): k is K => known.has(k))
    for (const k of defaultOrder) if (!order.includes(k)) order.push(k)
    return {
      ...current,
      ...p,
      order,
      hidden: (p.hidden ?? []).filter((k): k is K => known.has(k)),
    }
  }
}

/**
 * Factory for a per-studio dashboard board store. Persisted shape (state
 * fields + version) is identical across every store built from this
 * factory: only `mode`/`order`/`hidden` are meaningful, and the merge is
 * forward-compatible with future widget keys / unknown extra fields.
 */
export function createDashboardStore<K extends string>(opts: {
  persistKey: string
  defaultOrder: K[]
}): UseBoundStore<StoreApi<DashboardBoardState<K>>> {
  const { persistKey, defaultOrder } = opts
  return create<DashboardBoardState<K>>()(
    persist(
      (set) => ({
        mode: "pct",
        order: [...defaultOrder],
        hidden: [],
        setMode: (mode) => set({ mode }),
        moveWidget: (key, toIndex) =>
          set((state) => {
            const order = state.order.filter((k) => k !== key)
            order.splice(toIndex, 0, key)
            return { order }
          }),
        hideWidget: (key) =>
          set((state) => ({ hidden: [...new Set([...state.hidden, key])] })),
        showWidget: (key) =>
          set((state) => ({ hidden: state.hidden.filter((k) => k !== key) })),
      }),
      {
        name: persistKey,
        partialize: (state) => ({
          mode: state.mode,
          order: state.order,
          hidden: state.hidden,
        }),
        merge: createMergeDashboardState(defaultOrder),
      },
    ),
  )
}

/**
 * %/# board-wide toggle, read by widgets (via DualValue) without coupling
 * to a specific studio's store instance. Each dashboard page MUST wrap its
 * board in `<DashboardModeContext.Provider value={mode}>` sourced from its
 * own store — there is no meaningful default, so a missing provider throws
 * via `useDashboardMode` rather than silently rendering "pct".
 */
export const DashboardModeContext = createContext<DashboardNumberMode>(
  undefined as unknown as DashboardNumberMode,
)

export function useDashboardMode(): DashboardNumberMode {
  const mode = useContext(DashboardModeContext)
  if (mode === undefined) {
    throw new Error(
      "DashboardModeContext has no provider — wrap the board in <DashboardModeContext.Provider value={mode}>.",
    )
  }
  return mode
}

const LOCALES: Record<string, string> = {
  EN: "en-US",
  DE: "de-DE",
  JA: "ja-JP",
}

export function localeForLanguage(language: string | null | undefined): string {
  return LOCALES[language ?? "EN"] ?? "en-US"
}

export function formatInt(n: number, locale: string): string {
  return n.toLocaleString(locale)
}

export function formatPct(
  count: number,
  total: number,
  locale: string,
): string {
  if (total === 0) return "0%"
  const pct = (count / total) * 100
  // Below 1% keep one decimal (otherwise it rounds to 0%); above that, round
  // to a whole number — EXCEPT incomplete coverage (count < total) must never
  // round up to a misleading "100%": cap at 99 so e.g. 999/1000 reads "99%",
  // not "100%". Locale controls the decimal separator (e.g. "0,2%" in de-DE
  // vs "0.2%" in en-US).
  const rounded =
    pct > 0 && pct < 1
      ? pct
      : count < total
        ? Math.min(99, Math.round(pct))
        : Math.round(pct)
  return `${rounded.toLocaleString(locale, { maximumFractionDigits: 1 })}%`
}

/**
 * Parse a date-only "YYYY-MM-DD" string as LOCAL midnight rather than UTC
 * midnight, then format via the locale. `new Date("2026-07-18")` parses as
 * UTC midnight — in any timezone behind UTC (most of the Americas), that
 * displays as the PREVIOUS calendar day ("one-day-early" bug). Splitting the
 * components and constructing `new Date(y, m-1, d)` uses the LOCAL timezone
 * for both parsing and display, so the date-only value round-trips exactly.
 * Only for genuine date-only fields (e.g. dueDate) — NOT for tz-aware
 * timestamps (e.g. receivedAt), which already carry real time-of-day
 * information `new Date(iso)` parses correctly.
 */
export function formatIsoDate(isoDate: string, locale: string): string {
  const [y, m, d] = isoDate.split("-").map(Number)
  return new Date(y, m - 1, d).toLocaleDateString(locale)
}
